import React, { useCallback, useEffect, useState } from "react";
import { listPendingEdits, decidePendingEdit } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// (24/10/2026) "Duyệt" -> "XÁC NHẬN". Admin sửa TKB đã công bố thì ÁP DỤNG NGAY; mục sửa nằm ở "Chờ xác nhận" để GVCN lớp,
// GV vào dạy (và GV tiết gốc khi đổi tiết) bấm "Xác nhận" (đã biết lịch). Đủ người -> "Đã xác nhận".
// Yêu cầu do GV gửi (đổi tiết) nằm ở "Chờ Admin" — Admin đồng ý là áp dụng ngay, người còn lại chỉ xác nhận.
const STATUS_TABS = [
  { key: "applied", label: "🔔 Chờ xác nhận" },
  { key: "pending", label: "⏳ Chờ Admin đồng ý" },
  { key: "approved", label: "✅ Đã xác nhận" },
  { key: "rejected", label: "✖ Từ chối" },
];
const DAY_NAMES = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
const khoiOf = (n) => Number((String(n || "").match(/^\d+/) || [])[0]) || 0;
// Nhóm mục sửa theo (bản TKB, ngày). Đổi tiết (swap) nằm ở dòng của ngày gốc, và cũng được ghi vào dòng tổng hợp của ngày được đổi tới.
function groupByDay(items) {
  const multiRun = new Set(items.map((x) => x.runId)).size > 1;
  const groups = new Map();
  const get = (runId, day) => {
    const key = `${runId}|${day}`;
    if (!groups.has(key)) groups.set(key, { key, runId, day, rows: [], classes: new Map() });
    return groups.get(key);
  };
  items.forEach((it) => {
    const d = Number.isInteger(it.day) ? it.day : 9;
    const g = get(it.runId, d);
    g.rows.push(it);
    g.classes.set(it.className, (g.classes.get(it.className) || 0) + 1);
    if (it.kind === "swap" && Number.isInteger(it.toDay) && it.toDay !== d) {
      const g2 = get(it.runId, it.toDay);
      g2.classes.set(it.className, (g2.classes.get(it.className) || 0) + 1);
    }
  });
  const list = [...groups.values()];
  list.forEach((g) => g.rows.sort((a, b) => (a.period ?? 0) - (b.period ?? 0) || String(a.className).localeCompare(String(b.className), "vi", { numeric: true })));
  const order = (g) => Math.max(0, ...items.filter((x) => x.runId === g.runId).map((x) => x.createdAt || 0));
  list.sort((a, b) => order(b) - order(a) || a.runId.localeCompare(b.runId) || a.day - b.day);
  return { list, multiRun };
}
const fmtTime = (t) => (t ? new Date(t).toLocaleString("vi-VN", { hour12: false }) : "");

const HappySchoolPendingEdits = ({ runId = "", initialStatus = "applied", onChanged }) => {
  const schoolId = getActiveSchoolId();
  const [status, setStatus] = useState(initialStatus);
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [onlyRun, setOnlyRun] = useState(!!runId);
  const grouped = React.useMemo(() => groupByDay(items || []), [items]);

  const load = useCallback(() => {
    if (!schoolId) return;
    setItems(null);
    setError("");
    listPendingEdits(schoolId, status, onlyRun ? runId : "")
      .then((r) => setItems((r && r.items) || []))
      .catch((e) => setError(e.message));
  }, [schoolId, status, runId, onlyRun]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setStatus(initialStatus); }, [initialStatus]);

  const decide = async (it, action) => {
    let note = "";
    if (action === "reject") {
      const v = window.prompt("Lý do từ chối (có thể để trống):", "");
      if (v === null) return;
      note = v;
    } else if (action === "approve" && !window.confirm(`Đồng ý sửa ${it.className} — ${it.when}?\n${it.beforeText} → ${it.afterText}\n\n(Admin đồng ý là áp dụng ngay vào TKB.)`)) return;
    setBusy(it.id);
    setError("");
    try {
      const r = await decidePendingEdit(schoolId, it.runId, it.id, action, note);
      if (r && r.status === "pending") window.alert("Đã ghi nhận bạn đồng ý. Chờ Admin đồng ý mới áp dụng vào TKB.");
      load();
      onChanged && onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        <b>Admin sửa TKB</b> sau khi công bố là <b>áp dụng ngay</b>. Mục sửa nằm ở <b>Chờ xác nhận</b> để <b>GVCN lớp</b> và <b>GV vào dạy</b>
        (đổi tiết: cả GV tiết gốc) bấm <b>Xác nhận</b> — tức là đã biết lịch mới. Đủ người xác nhận thì sang <b>Đã xác nhận</b>.
        Yêu cầu đổi tiết do GV gửi nằm ở <b>Chờ Admin đồng ý</b> — Admin đồng ý là áp dụng ngay.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
        {STATUS_TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setStatus(t.key)} style={status === t.key ? tabActive : tabBtn}>{t.label}</button>
        ))}
        {runId && (
          <label style={{ fontSize: 13, color: "#374151", marginLeft: 8 }}>
            <input type="checkbox" checked={onlyRun} onChange={(e) => setOnlyRun(e.target.checked)} /> Chỉ bản TKB đang chọn
          </label>
        )}
        <button type="button" onClick={load} style={tabBtn}>↻ Tải lại</button>
      </div>
      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {items === null ? (
        <p>Đang tải...</p>
      ) : items.length === 0 ? (
        <p style={{ color: "#6b7280" }}>{status === "applied" ? "Không có thay đổi nào chờ xác nhận." : status === "pending" ? "Không có yêu cầu nào chờ Admin." : "Chưa có mục nào."}</p>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
            <thead>
              <tr>
                <th style={th}>Lớp</th>
                <th style={th}>Tiết</th>
                <th style={{ ...th, textAlign: "left" }}>Trước</th>
                <th style={{ ...th, textAlign: "left" }}>Sau khi sửa</th>
                <th style={{ ...th, textAlign: "left" }}>Lý do</th>
                <th style={{ ...th, textAlign: "left" }}>Người sửa</th>
                <th style={{ ...th, textAlign: "left" }}>Xác nhận</th>
                <th style={th}>{status === "pending" || status === "applied" ? "Thao tác" : "Thời gian"}</th>
              </tr>
            </thead>
            <tbody>
              {grouped.list.map((g) => {
                const classList = [...g.classes.entries()].sort((a, b) => khoiOf(a[0]) - khoiOf(b[0]) || String(a[0]).localeCompare(String(b[0]), "vi", { numeric: true }));
                const nRows = [...g.classes.values()].reduce((x, y) => x + y, 0);
                return (
                  <React.Fragment key={g.key}>
                    <tr>
                      <td colSpan={8} style={{ border: "1px solid #bae6fd", background: "#e0f2fe", padding: "7px 10px", textAlign: "left", fontSize: 13 }}>
                        <b>📅 {DAY_NAMES[g.day] || "Ngày khác"}{grouped.multiRun ? ` · bản TKB …${String(g.runId).slice(-4)}` : ""}</b>
                        {" — "}<b>{nRows}</b> thay đổi, <b>{classList.length}</b> lớp: {classList.map(([n, c]) => (c > 1 ? `${n} (${c})` : n)).join(", ")}
                      </td>
                    </tr>
                    {g.rows.map((it) => (
                <tr key={it.id} style={it.lastError && status === "pending" ? { background: "#fef2f2" } : {}}>
                  <td style={{ ...td, fontWeight: 700 }}>{it.className}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{it.when}</td>
                  <td style={{ ...td, textAlign: "left" }}>{it.beforeText}</td>
                  <td style={{ ...td, textAlign: "left", fontWeight: 600, color: "#1d4ed8" }}>{it.afterText}</td>
                  <td style={{ ...td, textAlign: "left" }}>{it.reason || ""}</td>
                  <td style={{ ...td, textAlign: "left" }}>{it.byName}<div style={{ color: "#9ca3af", fontSize: 11 }}>{fmtTime(it.createdAt)}</div></td>
                  <td style={{ ...td, textAlign: "left" }}>
                    {(it.approvalSteps || []).map((st) => (
                      <div key={st.role} style={{ color: st.done ? "#166534" : "#9a3412", whiteSpace: "nowrap" }}>
                        {st.done ? "✓" : "⏳"} {st.label}
                      </div>
                    ))}
                    {status === "rejected" ? <div style={{ color: "#b91c1c" }}>✖ Từ chối: {it.approvedByName}</div> : null}
                    {status !== "pending" && it.decidedNote ? <div style={{ color: "#6b7280", fontSize: 11 }}>{it.decidedNote}</div> : null}
                    {status === "pending" && it.lastError ? <div style={{ color: "#dc2626", fontSize: 11 }}>Lỗi lần đồng ý trước: {it.lastError}</div> : null}
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    {status === "applied" ? (
                      it.canConfirm ? (
                        <button type="button" disabled={busy === it.id} onClick={() => decide(it, "confirm")} style={btnOk}>{busy === it.id ? "..." : "Xác nhận"}</button>
                      ) : <span style={{ color: "#9ca3af" }}>Đã áp dụng — chờ người khác xác nhận</span>
                    ) : status === "pending" ? (
                      it.canApprove ? (
                        <>
                          <button type="button" disabled={busy === it.id} onClick={() => decide(it, "approve")} style={btnOk}>{busy === it.id ? "..." : "Đồng ý"}</button>
                          <button type="button" disabled={busy === it.id} onClick={() => decide(it, "reject")} style={btnNo}>Từ chối</button>
                        </>
                      ) : <span style={{ color: "#9ca3af" }}>Chờ Admin đồng ý</span>
                    ) : fmtTime(it.appliedAt || it.decidedAt)}
                  </td>
                </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const th = { border: "1px solid #e5e7eb", padding: "6px 8px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center" };
const td = { border: "1px solid #e5e7eb", padding: "5px 8px", textAlign: "center", verticalAlign: "top" };
const tabBtn = { padding: "7px 14px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const tabActive = { ...tabBtn, background: "#0ea5a3", color: "#fff", border: "1px solid #0ea5a3" };
const btnOk = { padding: "4px 10px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, marginRight: 6 };
const btnNo = { padding: "4px 10px", background: "#fff", color: "#b91c1c", border: "1px solid #fca5a5", borderRadius: 6, cursor: "pointer", fontWeight: 600 };

export default HappySchoolPendingEdits;
